import { serviceCodes } from '../../constants/disciplines';

/**
 * Patient documents library (Phase 8 §3.5, PAT-09).
 * Items `SK DOCUMENT#{id}` under the patient; files live in S3 and are uploaded with a pre-signed POST.
 */

/** Types a user can upload */
export type UploadablePatientDocumentType = 'PHYSICIAN_ORDER' | 'CONSENT' | 'EMERGENCY_PLAN' | 'GUARDIANSHIP' | 'ADVANCE_DIRECTIVE' | 'OTHER';
/** Generated by the server only (16E.1: the written discharge notice letter) */
export type GeneratedPatientDocumentType = 'DISCHARGE_NOTICE';
/**
 * Phase 19 F18: admission packet types. Signed in Pelora (server-rendered PDF) or uploaded as a
 * wet-signed scan with the signer's name and the signing date.
 */
export type PacketDocumentType =
  | 'RIGHTS_NOTICE'
  | 'COMPLAINT_NOTICE'
  | 'INFORMED_CONSENT'
  | 'ABUSE_POLICY_ACK'
  | 'ADVANCE_DIRECTIVE_NOTICE'
  | 'NPP_ACK'
  | 'PHI_SHARING_CONSENT'
  | 'CLIENT_CHOICE';
export type PatientDocumentType = UploadablePatientDocumentType | GeneratedPatientDocumentType | PacketDocumentType;

export const PACKET_DOCUMENT_TYPES: PacketDocumentType[] = [
  'RIGHTS_NOTICE',
  'COMPLAINT_NOTICE',
  'INFORMED_CONSENT',
  'ABUSE_POLICY_ACK',
  'ADVANCE_DIRECTIVE_NOTICE',
  'NPP_ACK',
  'PHI_SHARING_CONSENT',
  'CLIENT_CHOICE',
];

/** Packet types that grant consent: only a guardian with canConsent (or an adult patient) may sign */
export const CONSENT_PACKET_TYPES: PacketDocumentType[] = ['INFORMED_CONSENT', 'PHI_SHARING_CONSENT', 'CLIENT_CHOICE'];

export const isPacketDocumentType = (type: string): type is PacketDocumentType =>
  (PACKET_DOCUMENT_TYPES as string[]).includes(type);

/** Services an informed consent is signed for (one per active service) */
// Phase 20F: every active visit service of the registry (matches the backend)
export type PacketService = string;
export const PACKET_SERVICES: PacketService[] = serviceCodes();

/** Upload choices */
export const PATIENT_DOCUMENT_TYPES: UploadablePatientDocumentType[] = [
  'PHYSICIAN_ORDER',
  'CONSENT',
  'EMERGENCY_PLAN',
  'GUARDIANSHIP',
  // Phase 20F: the signed directive or out-of-hospital DNR form
  'ADVANCE_DIRECTIVE',
  'OTHER',
];

export const PATIENT_DOCUMENT_TYPE_LABELS: Record<PatientDocumentType, string> = {
  PHYSICIAN_ORDER: 'Physician order',
  CONSENT: 'Consent',
  EMERGENCY_PLAN: 'Emergency plan',
  GUARDIANSHIP: 'Guardianship',
  ADVANCE_DIRECTIVE: 'Advance directive',
  OTHER: 'Other',
  DISCHARGE_NOTICE: 'Discharge notice',
  RIGHTS_NOTICE: 'Client rights notice',
  COMPLAINT_NOTICE: 'Complaint notice',
  INFORMED_CONSENT: 'Consent to services',
  ABUSE_POLICY_ACK: 'Abuse reporting acknowledgement',
  ADVANCE_DIRECTIVE_NOTICE: 'Advance directives notice',
  NPP_ACK: 'Privacy notice acknowledgement',
  PHI_SHARING_CONSENT: 'Consent to share health information',
  CLIENT_CHOICE: 'Choice of provider',
};

/** Filter choices: every type that can appear in the list */
export const LISTED_PATIENT_DOCUMENT_TYPES: PatientDocumentType[] = [...PATIENT_DOCUMENT_TYPES, 'DISCHARGE_NOTICE', ...PACKET_DOCUMENT_TYPES];

/** Who signed a packet document */
export interface PacketDocumentSigner {
  signerType?: 'GUARDIAN' | 'PATIENT';
  guardianId?: string | null;
  name: string;
  relationship?: string;
  legalAuthority?: string;
}

/** A document as listed by GET /patientmr/{patientId}/documents */
export interface PatientDocument {
  documentId: string;
  type: PatientDocumentType;
  documentDate: string;   // YYYY-MM-DD
  title: string;
  documentName?: string;
  contentType: string;
  size: number;
  uploadedBy: string;
  uploadedByName?: string;
  uploadedAt: string;     // ISO
  /** Phase 19 F18: packet documents only */
  service?: PacketService | null;
  signer?: PacketDocumentSigner;
  signedAt?: string;
  signatureMethod?: 'DRAWN' | 'UPLOADED_SCAN';
}

/** POST /patientmr/{patientId}/documents — confirm a pre-signed upload */
export interface CreatePatientDocumentRequest {
  key: string;
  documentName: string;
  type: PatientDocumentType;
  documentDate: string;
  title: string;
  /** Packet scans (wet-signed): who signed; documentDate is the signing date */
  signerName?: string;
  signerRelationship?: string;
  service?: PacketService;
}

/** Metadata the upload dialog collects */
export interface PatientDocumentMetadata {
  type: PatientDocumentType;
  documentDate: string;
  title: string;
  signerName?: string;
  signerRelationship?: string;
  service?: PacketService;
}

export interface ListPatientDocumentsResponse {
  documents: PatientDocument[];
}

export interface PatientDocumentUrlResponse {
  url: string;
}

// --- Phase 19 F18: admission packet ---------------------------------------------

/** Template shown before signing (Pelora default text unless the agency overrides it) */
export interface AdmissionPacketTemplate {
  type: PacketDocumentType;
  title: string;
  body: string;
  version: string;
  required: boolean;
  perService: boolean;
  isDefaultText: boolean;
}

/** One required packet item and its signed document (null when missing) */
export interface AdmissionPacketItem {
  type: PacketDocumentType;
  service: PacketService | null;
  documentId: string | null;
  signedAt: string | null;
  signerName: string | null;
}

export interface AdmissionPacketGuardian {
  guardianId: string;
  name: string;
  relationship: string;
  canConsent: boolean;
}

/** GET /patientmr/{patientId}/documents?view=packet */
export interface AdmissionPacket {
  templates: Partial<Record<PacketDocumentType, AdmissionPacketTemplate>>;
  items: AdmissionPacketItem[];
  complete: boolean;
  guardians: AdmissionPacketGuardian[];
}

export type AdmissionPacketSignerInput = { guardianId: string } | { signerType: 'PATIENT'; name: string };

/** POST /patientmr/{patientId}/documents (mode SIGN): the witness is the logged-in user */
export interface SignAdmissionDocumentRequest {
  mode: 'SIGN';
  type: PacketDocumentType;
  service?: PacketService;
  signer: AdmissionPacketSignerInput;
  /** 'data:image/png;base64,...' */
  signatureBase64: string;
}
